/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Aug 06 2024
 **/

import { useRef } from "react";

import { v4 } from "uuid";

import { Accordion } from "./Accordion";
import AccordionContainer from "./AccordionContainer";
import useAccordion from "./useAccordion";

export type AccordionStateProps = {
	isAccordionOpen?: boolean;
};

type accordionOptions = {
	container: Omit<React.ComponentProps<typeof AccordionContainer>, "children">;
	accordion: React.ComponentProps<typeof Accordion>;
	header?: {
		option?: Omit<React.ComponentProps<typeof Accordion.Header>, "children">;
		render: (props: React.PropsWithChildren<AccordionStateProps>) => React.ReactNode;
	};
	body?: {
		option?: Omit<React.ComponentProps<typeof Accordion.Body>, "children">;
		render: (props: React.PropsWithChildren<AccordionStateProps>) => React.ReactNode;
	};
};

type AccordionInnerTemplate = {
	activeID: string;
};

export interface TemplateProps extends accordionOptions {}

export default function AccordionTemplate(props: TemplateProps) {
	const uniqID = useRef(v4());

	const uniqProps = {
		...props,
		container: {
			...props.container,
			activeId: `r-${props.container.activeId}-${uniqID.current}`
		},
		accordion: {
			...props.accordion,
			id: `r-${props.accordion.id}-${uniqID.current}`
		},
		header: props.header && {
			...props.header,
			option: {
				...props.header.option,
				id: `r-${props.accordion.id}-${uniqID.current}`
			}
		}
	};

	return (
		<AccordionContainer {...uniqProps.container}>
			<AccordionInnerTemplate {...uniqProps} />
		</AccordionContainer>
	);
}

/**
 * @description AccordionContainer가 선언된 컴포넌트에서 useAccordion을 호출할 없음으로 AccordionInnerTemplate을 생성하여 사용
 */
function AccordionInnerTemplate(props: TemplateProps) {
	const { data } = useAccordion();

	return (
		<Accordion {...props.accordion}>
			{props.header && (
				<Accordion.Header {...props.header.option}>
					{props.header.render({ isAccordionOpen: Boolean(data?.[props.container.activeId!]) })}
				</Accordion.Header>
			)}
			{props.body && (
				<Accordion.Body
					{...props.body.option}
					id={props.header?.option?.id}
					onClick={(e) => e.stopPropagation()}
				>
					{props.body.render({ isAccordionOpen: Boolean(data?.[props.container.activeId!]) })}
				</Accordion.Body>
			)}
		</Accordion>
	);
}
